import { useEffect, useMemo, useRef, useState } from 'react'
import { ArrowDown, ArrowUp, Camera, RefreshCw, Search, Sparkles, Star, Trash2, Undo2 } from 'lucide-react'
import { Button } from '../lib/shadcn/button'
import { Input } from '../lib/shadcn/input'
import { toast } from '../lib/shadcn/sonner'
import {
  useGetStockStation,
  useSaveStockStation,
  useSetStockStationPhotos,
} from '../hooks/backend/stockStation'
import {
  useCleanupProductPhoto,
  useGetPhotoOriginals,
  usePublishLabelProducts,
  useUploadProductPhoto,
} from '../hooks/backend/inventoryPage'
import { useGetProductDefaults } from '../hooks/backend/settingsPage'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '../lib/shadcn/sheet'
import { publishLabels } from '../utils/publishLabels'
import { setHomeScreenTitle } from '../utils/homeScreenApp'

// Stock Station (/stock): a phone screen for quick stock updates, full
// screen like the Scan Station. Per flavor: the unbagged batch in ounces
// (Special Order), and per size the measured bag weight and bagged count.
// Each number saves when you leave the box or press Enter. Each flavor's
// photos can be added, reordered, removed or cleaned up with AI too.

type Row = {
  sku: string
  item_name: string | null
  label_name: string | null
  wholesale_tier: string | null
  weight_oz: number | string | null
  actual_weight_oz: number | string | null
  qty_in_stock: number | string | null
  unbagged_oz: number | string | null
  photos: unknown
  image_url: string | null
  website_stock: number | null
}

const photoList = (row: Row): string[] => {
  const list = Array.isArray(row.photos) ? (row.photos as unknown[]).map(String).filter(Boolean) : []
  return list.length ? list : row.image_url ? [row.image_url] : []
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = String(reader.result ?? '')
      resolve(result.slice(result.indexOf(',') + 1))
    }
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })
}

// The flavor's photos on its card; tap to edit them in a sheet: add (camera
// or library), make featured, move, clean up with AI (and put the original
// back), or remove. Save writes them for both sizes and publishes the
// flavor to its Publish To stores, the same as saving Edit Product.
function PhotoStrip({ row, onSaved }: { row: Row; onSaved: (photos: string[]) => void }) {
  const photos = photoList(row)
  const [open, setOpen] = useState(false)
  const name = String(row.label_name || row.item_name || row.sku)
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-3 flex w-full items-center gap-2 overflow-x-auto rounded-md pb-1 text-left"
      >
        {photos.map(url => (
          <img key={url} src={url} alt="" className="h-14 w-14 shrink-0 rounded-md border object-cover" />
        ))}
        <span className="inline-flex h-14 shrink-0 items-center rounded-md border px-3 text-sm font-medium">
          <Camera className="mr-1.5 h-4 w-4" />
          {photos.length ? 'Edit photos' : 'Add photos'}
        </span>
      </button>
      {open && (
        <PhotoEditor
          row={row}
          name={name}
          start={photos}
          onClose={() => setOpen(false)}
          onSaved={next => {
            onSaved(next)
            setOpen(false)
          }}
        />
      )}
    </>
  )
}

function PhotoEditor({
  row,
  name,
  start,
  onClose,
  onSaved,
}: {
  row: Row
  name: string
  start: string[]
  onClose: () => void
  onSaved: (photos: string[]) => void
}) {
  const upload = useUploadProductPhoto()
  const cleanup = useCleanupProductPhoto()
  const getOriginals = useGetPhotoOriginals()
  const setPhotos = useSetStockStationPhotos()
  const publish = usePublishLabelProducts()
  const inputRef = useRef<HTMLInputElement>(null)
  const [photos, setDraft] = useState<string[]>(start)
  const [originals, setOriginals] = useState<Record<string, string>>({})
  const [cleaning, setCleaning] = useState<string[]>([])
  const [uploading, setUploading] = useState(false)
  const [saving, setSaving] = useState(false)
  // The "coming soon" photo from Settings > Products > New product defaults;
  // a real photo replaces it.
  const getDefaults = useGetProductDefaults()
  const defaultPhoto = useRef<string | null>(null)

  useEffect(() => {
    getDefaults
      .trigger({})
      .then((res: unknown) => {
        defaultPhoto.current = (res as { photo_url?: string | null } | undefined)?.photo_url || null
      })
      .catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (start.length === 0) return
    getOriginals
      .trigger({ urls: start })
      .then((res: unknown) => setOriginals((res as { originals?: Record<string, string> })?.originals ?? {}))
      .catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const changed = photos.join('\n') !== start.join('\n')
  const busy = uploading || saving || cleaning.length > 0

  const onFiles = async (files: File[]) => {
    const images = files.filter(f => f.type.startsWith('image/'))
    if (images.length === 0) return
    setUploading(true)
    let failed = 0
    for (const file of images) {
      try {
        const res = (await upload.trigger({
          fileName: file.name || 'photo.jpg',
          data: await fileToBase64(file),
          mimeType: file.type,
        })) as { url?: string } | undefined
        if (res?.url) setDraft(prev => [...prev.filter(p => p !== defaultPhoto.current), res.url!])
        else failed++
      } catch {
        failed++
      }
    }
    if (failed) toast.error(failed === 1 ? 'A photo failed to upload' : `${failed} photos failed to upload`)
    setUploading(false)
    if (inputRef.current) inputRef.current.value = ''
  }

  const move = (url: string, by: number) =>
    setDraft(prev => {
      const i = prev.indexOf(url)
      const j = i + by
      if (i < 0 || j < 0 || j >= prev.length) return prev
      const next = [...prev]
      next[i] = prev[j]!
      next[j] = url
      return next
    })

  const feature = (url: string) => setDraft(prev => [url, ...prev.filter(p => p !== url)])
  const remove = (url: string) => setDraft(prev => prev.filter(p => p !== url))
  const useOriginal = (url: string) => {
    const original = originals[url]
    if (original) setDraft(prev => prev.map(p => (p === url ? original : p)))
  }

  const clean = async (url: string) => {
    setCleaning(prev => [...prev, url])
    try {
      const res = (await cleanup.trigger({ url })) as { url?: string; originalUrl?: string } | undefined
      if (!res?.url) throw new Error('Photo cleanup failed.')
      setDraft(prev => prev.map(p => (p === url ? res.url! : p)))
      if (res.originalUrl) setOriginals(prev => ({ ...prev, [res.url!]: res.originalUrl! }))
      toast.success('Photo cleaned up. Use Original to put it back.')
    } catch (e) {
      toast.error(`${e instanceof Error ? e.message : 'Photo cleanup failed.'} The original photo was kept.`)
    } finally {
      setCleaning(prev => prev.filter(p => p !== url))
    }
  }

  const save = async () => {
    setSaving(true)
    try {
      const res = (await setPhotos.trigger({ sku: row.sku, photos })) as { label_name?: string | null }
      onSaved(photos)
      toast.success('Photos saved')
      if (res?.label_name) void publishLabels([res.label_name], p => publish.trigger(p))
    } catch (e) {
      toast.error(`Couldn't save the photos: ${e instanceof Error ? e.message : String(e)}`)
      setSaving(false)
    }
  }

  return (
    <Sheet open onOpenChange={o => !o && !busy && onClose()}>
      <SheetContent side="bottom" className="max-h-[90vh] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{name} photos</SheetTitle>
          <SheetDescription>
            The first photo is the featured one. Small and Large share these photos.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-3">
          {photos.length === 0 && <p className="text-sm text-muted-foreground">No photos yet.</p>}
          {photos.map((url, i) => {
            const isCleaning = cleaning.includes(url)
            return (
              <div key={url} className="flex gap-3 rounded-lg border p-2">
                <img
                  src={url}
                  alt=""
                  className={`h-24 w-24 shrink-0 rounded-md object-cover ${isCleaning ? 'opacity-50' : ''}`}
                />
                <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                  <div className="text-xs text-muted-foreground">
                    {i === 0 ? 'Featured' : `Photo ${i + 1}`}
                    {isCleaning ? ' · cleaning up…' : ''}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    <Button size="icon" variant="outline" aria-label="Move up" disabled={busy || i === 0} onClick={() => move(url, -1)}>
                      <ArrowUp className="h-4 w-4" />
                    </Button>
                    <Button size="icon" variant="outline" aria-label="Move down" disabled={busy || i === photos.length - 1} onClick={() => move(url, 1)}>
                      <ArrowDown className="h-4 w-4" />
                    </Button>
                    <Button size="icon" variant="outline" aria-label="Make featured" disabled={busy || i === 0} onClick={() => feature(url)}>
                      <Star className="h-4 w-4" />
                    </Button>
                    <Button size="icon" variant="outline" aria-label="Remove" disabled={busy} onClick={() => remove(url)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    <Button size="sm" variant="outline" disabled={busy} onClick={() => void clean(url)}>
                      <Sparkles className="mr-1 h-4 w-4" />
                      {isCleaning ? 'Cleaning…' : 'Clean up'}
                    </Button>
                    {originals[url] && (
                      <Button size="sm" variant="outline" disabled={busy} onClick={() => useOriginal(url)}>
                        <Undo2 className="mr-1 h-4 w-4" />
                        Original
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            )
          })}

          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={e => void onFiles(Array.from(e.target.files ?? []))}
          />
          <Button variant="outline" className="h-12 w-full" disabled={busy} onClick={() => inputRef.current?.click()}>
            <Camera className="mr-1.5 h-4 w-4" />
            {uploading ? 'Uploading…' : 'Add photos'}
          </Button>
        </div>

        <SheetFooter className="mt-4 flex-row gap-2">
          <Button variant="outline" className="h-12 flex-1" disabled={busy} onClick={onClose}>
            Cancel
          </Button>
          <Button className="h-12 flex-1" disabled={busy || !changed} onClick={() => void save()}>
            {saving ? 'Saving…' : 'Save & publish'}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}

type Field = 'actual_weight_oz' | 'qty_in_stock' | 'unbagged_oz'

const isSpecial = (tier: unknown) => String(tier ?? '').trim().toLowerCase() === 'special order'

const sizeName = (row: Row) => {
  const name = String(row.item_name ?? '').trim()
  const m = name.match(/(small|large)$/i)
  return m ? m[1]!.charAt(0).toUpperCase() + m[1]!.slice(1).toLowerCase() : name
}

const text = (v: unknown) => (v == null || v === '' ? '' : String(Number(v)))

// A number box that saves on blur or Enter, and shows the saved value
// again if the save fails.
function NumberBox({
  id,
  value,
  placeholder,
  step,
  onSave,
  big,
  stepper,
}: {
  id: string
  value: string
  placeholder?: string
  step?: string
  onSave: (next: number | null) => Promise<boolean>
  big?: boolean
  /** − and + buttons beside the box; taps save after a short pause. */
  stepper?: boolean
}) {
  const [draft, setDraft] = useState(value)
  const [saving, setSaving] = useState(false)
  const last = useRef(value)
  const draftRef = useRef(value)
  draftRef.current = draft
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    setDraft(value)
    last.current = value
  }, [value])

  const commit = async () => {
    if (timer.current) {
      clearTimeout(timer.current)
      timer.current = null
    }
    const t = draftRef.current.trim()
    if (t === last.current) return
    const n = t === '' ? null : Number(t)
    if (n != null && (!Number.isFinite(n) || n < 0)) {
      toast.error('Enter a number of 0 or more')
      setDraft(last.current)
      return
    }
    setSaving(true)
    const ok = await onSave(n)
    setSaving(false)
    if (ok) last.current = t
    else setDraft(last.current)
  }

  // Several quick taps save once, after the last one.
  const bump = (by: number) => {
    const next = String(Math.max(0, Math.round((Number(draftRef.current) || 0) + by)))
    draftRef.current = next
    setDraft(next)
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => void commit(), 700)
  }

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current)
  }, [])

  const box = (
    <Input
      id={id}
      type="number"
      inputMode={step === "1" ? "numeric" : "decimal"}
      min="0"
      step={step ?? 'any'}
      value={draft}
      placeholder={placeholder}
      disabled={saving}
      onChange={e => setDraft(e.target.value)}
      // Tapping in selects the number, so typing replaces it (no erasing the 0).
      onFocus={e => e.target.select()}
      onBlur={() => void commit()}
      onKeyDown={e => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
      }}
      className={`${big ? 'h-12 text-lg' : 'h-11 text-base'} ${stepper ? 'min-w-0 px-1 text-center' : ''}`}
    />
  )

  if (!stepper) return box

  return (
    <div className="flex items-center gap-1">
      <Button
        type="button"
        variant="outline"
        className="h-11 w-10 shrink-0 px-0 text-lg"
        aria-label="One less"
        disabled={saving}
        onClick={() => bump(-1)}
      >
        −
      </Button>
      {box}
      <Button
        type="button"
        variant="outline"
        className="h-11 w-10 shrink-0 px-0 text-lg"
        aria-label="One more"
        disabled={saving}
        onClick={() => bump(1)}
      >
        +
      </Button>
    </div>
  )
}

export default function StockStationPage() {
  const getStock = useGetStockStation()
  const saveStock = useSaveStockStation()
  const [rows, setRows] = useState<Row[]>([])
  const [query, setQuery] = useState('')
  const [specialOnly, setSpecialOnly] = useState(false)

  useEffect(() => {
    const previousTitle = document.title
    document.title = 'Stock Station'
    setHomeScreenTitle('Stock Station')
    return () => {
      document.title = previousTitle
      setHomeScreenTitle("Poppin' Hub")
    }
  }, [])

  const load = async () => {
    try {
      const data = (await getStock.trigger({})) as Row[] | null
      setRows(Array.isArray(data) ? data : [])
    } catch (e) {
      toast.error(`Couldn't load products: ${e instanceof Error ? e.message : String(e)}`)
    }
  }

  useEffect(() => {
    void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const save = async (row: Row, field: Field, value: number | null): Promise<boolean> => {
    try {
      const res = (await saveStock.trigger({ sku: row.sku, field, value })) as {
        website_stock?: Record<string, number>
        failures?: string[]
      }
      const web = res?.website_stock ?? {}
      setRows(prev =>
        prev.map(r => {
          const sameFlavor =
            field === 'unbagged_oz' && row.label_name && r.label_name === row.label_name
          const next = r.sku === row.sku || sameFlavor ? { ...r, [field]: value } : r
          return r.sku in web ? { ...next, website_stock: web[r.sku] ?? 0 } : next
        })
      )
      if (res?.failures?.length) {
        toast.error(`Saved, but the website didn't update: ${res.failures.join('; ')}`)
      } else {
        toast.success('Saved')
      }
      return true
    } catch (e) {
      toast.error(`Couldn't save: ${e instanceof Error ? e.message : String(e)}`)
      return false
    }
  }

  const flavors = useMemo(() => {
    const q = query.trim().toLowerCase()
    const groups = new Map<string, Row[]>()
    for (const r of rows) {
      if (specialOnly && !isSpecial(r.wholesale_tier)) continue
      const key = String(r.label_name || r.item_name || r.sku)
      if (q && !key.toLowerCase().includes(q) && !String(r.sku).includes(q)) continue
      groups.set(key, [...(groups.get(key) ?? []), r])
    }
    // Small before Large.
    return [...groups.entries()].map(([name, sizes]) => ({
      name,
      sizes: [...sizes].sort((a, b) => sizeName(b).localeCompare(sizeName(a))),
    }))
  }, [rows, query, specialOnly])

  return (
    <div className="mx-auto max-w-xl px-4 pb-10">
      <div className="sticky top-0 z-10 -mx-4 space-y-2 border-b bg-background px-4 pb-3 pt-3">
        <div className="flex items-center gap-2">
          <h1 className="flex-1 text-lg font-semibold">Stock Station</h1>
          <a href="/" className="text-sm text-muted-foreground underline">
            Hub
          </a>
          <Button
            variant="outline"
            size="icon"
            aria-label="Refresh"
            onClick={() => void load()}
            disabled={getStock.loading}
          >
            <RefreshCw className={`h-4 w-4 ${getStock.loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Find a flavor"
            className="h-11 pl-9 text-base"
          />
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant={specialOnly ? 'outline' : 'default'}
            onClick={() => setSpecialOnly(false)}
          >
            All flavors
          </Button>
          <Button
            size="sm"
            variant={specialOnly ? 'default' : 'outline'}
            onClick={() => setSpecialOnly(true)}
          >
            Special Order
          </Button>
        </div>
      </div>

      {getStock.loading && rows.length === 0 ? (
        <p className="py-8 text-center text-sm text-muted-foreground">Loading…</p>
      ) : flavors.length === 0 ? (
        <p className="py-8 text-center text-sm text-muted-foreground">No flavors match.</p>
      ) : (
        <div className="mt-3 space-y-3">
          {flavors.map(({ name, sizes }) => {
            const special = sizes.some(s => isSpecial(s.wholesale_tier))
            const first = sizes[0]!
            const batch = sizes.find(s => s.unbagged_oz != null)?.unbagged_oz ?? null
            return (
              <div key={name} className="rounded-lg border bg-card p-3">
                <div className="mb-2 flex items-baseline justify-between gap-2">
                  <h2 className="font-semibold">{name}</h2>
                  {first.wholesale_tier && (
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {first.wholesale_tier}
                    </span>
                  )}
                </div>

                {special && (
                  <div className="mb-3 space-y-1">
                    <label htmlFor={`batch-${first.sku}`} className="text-sm font-medium">
                      Unbagged popcorn (oz)
                    </label>
                    <NumberBox
                      id={`batch-${first.sku}`}
                      big
                      value={text(batch)}
                      placeholder="0"
                      onSave={v => save(first, 'unbagged_oz', v)}
                    />
                    <p className="text-xs text-muted-foreground">
                      Website shows{' '}
                      {sizes
                        .map(s => `${s.website_stock ?? 0} ${sizeName(s)}`)
                        .join(' · ')}
                    </p>
                  </div>
                )}

                <div className="space-y-2">
                  {sizes.map(s => (
                    <div key={s.sku} className="grid grid-cols-[3.25rem_minmax(0,0.8fr)_minmax(0,1.2fr)] items-end gap-2">
                      <div className="pb-3 text-sm font-medium">{sizeName(s)}</div>
                      <div className="space-y-1">
                        <label htmlFor={`w-${s.sku}`} className="text-xs text-muted-foreground">
                          Bag weight (oz)
                        </label>
                        <NumberBox
                          id={`w-${s.sku}`}
                          value={text(s.actual_weight_oz ?? s.weight_oz)}
                          onSave={v => save(s, 'actual_weight_oz', v)}
                        />
                      </div>
                      <div className="space-y-1">
                        <label htmlFor={`q-${s.sku}`} className="text-xs text-muted-foreground">
                          Bagged
                        </label>
                        <NumberBox
                          id={`q-${s.sku}`}
                          step="1"
                          stepper
                          value={text(s.qty_in_stock ?? 0)}
                          onSave={v => save(s, 'qty_in_stock', v)}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                <PhotoStrip
                  row={first}
                  onSaved={photos =>
                    setRows(prev =>
                      prev.map(r =>
                        r.sku === first.sku ||
                        (first.label_name && r.label_name === first.label_name)
                          ? { ...r, photos, image_url: r.image_url || photos[0] || null }
                          : r
                      )
                    )
                  }
                />
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
